import React from "react";

// Mirrors the server's validation in PreliminaryFormSubmissionController.
// ValidateApprovalBranches plus the NOT NULL fields enforced at the DB layer.

export const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isBlank = (value) => value == null || String(value).trim() === "";

// PhoneField stores raw digits only (formatting is display-only), so a valid
// phone is exactly 10 digits.
const isCompletePhoneNumber = (value) => /^\d{10}$/.test(String(value || ""));

// Required scalar fields on a subagency entry, in the order they appear in the
// SubagencyCard. Each tuple is [fieldName, userVisibleLabel].
const subagencyRequiredFields = [
  ["accountType", "Account Type"],
  ["active", "Active?"],
  ["chargeStatus", "Charge Status"],
  ["chargeCategory", "Charge Category"],
  ["serviceDesc", "Service Description"],
  ["estParcels", "Estimated Parcels"],
  ["estAmount", "Estimated Amount"],
  ["enablingStatute", "Enabling Statute/Code Section"],
  ["legalAuthorityType", "Legal Authority Type"],
  ["approvalMethod", "Approval Method"],
  ["legalInPlace", "Is Legal Authority currently in place?"]
];

// Walks the form state and returns an array of every user-visible error in the
// order they appear (Step 1 → 4, top → bottom). Empty array means the form is
// valid. Each error carries the field path used by ValidationContext to
// highlight the offending input, plus the step index so the caller can jump to
// it. Only signer rows whose agreementRequired is true are user-visible (see
// AuthorizedSignerStep), so only those are checked. First-error-wins per
// field: emit "Email is required" *or* "Email is invalid", not both.
export function validateSubmission({ selection, preparer, entries, signers, confirm }) {
  const errors = [];

  // Step 1 — Agency Info
  if (!selection) {
    errors.push({ path: "selection", step: 0, message: "Select a parent agency." });
  }

  // Step 2 — Direct Charges
  const filled = entries.filter((entry) => entry.subagency);
  if (filled.length === 0) {
    errors.push({ path: "entries", step: 1, message: "Add at least one subagency entry." });
  }

  for (let index = 0; index < filled.length; index++) {
    const entry = filled[index];
    const where = `Subagency ${index + 1}`;
    const entryPath = (field) => `entries[${entry.id}].${field}`;

    for (const [field, label] of subagencyRequiredFields) {
      if (isBlank(entry[field])) {
        errors.push({ path: entryPath(field), step: 1, message: `${where}: ${label} is required.` });
      }
    }

    if (entry.approvalMethod === "Voter Approved" && isBlank(entry.electionDate)) {
      errors.push({
        path: entryPath("electionDate"),
        step: 1,
        message: `${where}: Election Date is required for Voter Approved.`
      });
    }
    if (entry.approvalMethod === "Property Owner Approved" && isBlank(entry.approvalDate)) {
      errors.push({
        path: entryPath("approvalDate"),
        step: 1,
        message: `${where}: Approval Date is required for Property Owner Approved.`
      });
    }
    if (entry.approvalMethod === "Board Adopted") {
      if (isBlank(entry.adoptionType)) {
        errors.push({
          path: entryPath("adoptionType"),
          step: 1,
          message: `${where}: Adoption Type is required for Board Adopted.`
        });
      }
      if (entry.adoptionType === "Annual" && isBlank(entry.adoptionDate)) {
        errors.push({
          path: entryPath("adoptionDate"),
          step: 1,
          message: `${where}: Adoption Date is required for Annual adoption.`
        });
      }
      if (entry.adoptionType === "Evergreen" && isBlank(entry.origAdoptionDate)) {
        errors.push({
          path: entryPath("origAdoptionDate"),
          step: 1,
          message: `${where}: Original Adoption Date is required for Evergreen adoption.`
        });
      }
    }

    if (entry.reqDescUpdate && isBlank(entry.newDescription)) {
      errors.push({
        path: entryPath("newDescription"),
        step: 1,
        message: `${where}: New Description is required when Request Description Update is checked.`
      });
    }
  }

  // Step 3 — Authorized Signer (visible-index path matches AuthorizedSignerStep's render order)
  const visibleSigners = (signers || []).filter((signer) => signer.agreementRequired);
  for (let index = 0; index < visibleSigners.length; index++) {
    const signer = visibleSigners[index];
    const where = visibleSigners.length > 1 ? `Signer ${index + 1}` : "Authorized Signer";
    if (isBlank(signer.phone)) {
      errors.push({ path: `signers[${index}].phone`, step: 2, message: `${where}: Phone is required.` });
    } else if (!isCompletePhoneNumber(signer.phone)) {
      errors.push({
        path: `signers[${index}].phone`,
        step: 2,
        message: `${where}: Phone must be a complete 10-digit number.`
      });
    }
    if (isBlank(signer.email)) {
      errors.push({ path: `signers[${index}].email`, step: 2, message: `${where}: Email is required.` });
    } else if (!emailPattern.test(signer.email.trim())) {
      errors.push({
        path: `signers[${index}].email`,
        step: 2,
        message: `${where}: Email is not a valid address.`
      });
    }
  }

  // Step 4 — Certification
  if (isBlank(preparer.first)) {
    errors.push({ path: "preparer.first", step: 3, message: "Form Preparer First Name is required." });
  }
  if (isBlank(preparer.last)) {
    errors.push({ path: "preparer.last", step: 3, message: "Form Preparer Last Name is required." });
  }
  if (isBlank(preparer.phone)) {
    errors.push({ path: "preparer.phone", step: 3, message: "Form Preparer Phone is required." });
  } else if (!isCompletePhoneNumber(preparer.phone)) {
    errors.push({
      path: "preparer.phone",
      step: 3,
      message: "Form Preparer Phone must be a complete 10-digit number."
    });
  }
  if (isBlank(preparer.email)) {
    errors.push({ path: "preparer.email", step: 3, message: "Form Preparer Email is required." });
  } else if (!emailPattern.test(preparer.email.trim())) {
    errors.push({
      path: "preparer.email",
      step: 3,
      message: "Form Preparer Email is not a valid address."
    });
  }

  if (confirm && !confirm.preparer && !confirm.contacts && !confirm.signer) {
    errors.push({
      path: "confirm",
      step: 3,
      message: "Select at least one recipient for the confirmation email."
    });
  }

  return errors;
}

// Empty array pre-Submit. PreliminaryNotification.jsx swaps in the live error
// list after the user clicks Submit; primitives self-style red on match.
export const ValidationContext = React.createContext([]);

export function useValidationError(path) {
  const errors = React.useContext(ValidationContext);
  if (!path) return null;
  return errors.find((e) => e.path === path) || null;
}
